import axios from "axios";
import { ENVIRONMENT_TYPE } from "./common";
import { decryptData } from "../utils/api/encryption";
import { consoleLog } from "../utils/consoleLog/consoleLog";

export const ENVIRONMENT: any = {
  ENCRYPTION_KEY: process.env.REACT_APP_ENCRYPTION_KEY || "",
  STREAMING_ENABLE: process.env.REACT_APP_STREAMING_ENABLE || true,
  environment: process.env.REACT_APP_ENVIRONMENT || ENVIRONMENT_TYPE.LOCAL,
  SOCKET_URL: process.env.REACT_APP_SOCKET_URL || "",
  APP_URL: process.env.REACT_APP_APP_URL || "",
  WEB_URL: process.env.REACT_APP_WEB_URL || "",
  ENDPOINT: process.env.REACT_APP_ENDPOINT || "http://localhost:3000",
  CLOUDFARE_VERIFICATION_ID: "",
  GOOGLE_AUTH_CLIENT_ID: "",
  GOOGLE_ANALYTICS_MEASUREMENT_ID: process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID || "",
  GOOGLE_MAP_API_KEY: (process.env.REACT_APP_GOOGLE_MAP_API_KEY || "").trim(),
  PUSHER_KEY: process.env.REACT_APP_PUSHER_KEY || "",
  PUSHER_CLUSTER: process.env.REACT_APP_PUSHER_CLUSTER || "",
  WEATHER_API: {
    API_KEY: "",
  },
  RADAR: {
    API_KEY: "",
  },
  IS_ENV_LOADED: false,
  ENABLE_CRYPTO: process.env.REACT_APP_ENABLE_CRYPTO || true,
  PENDO_KEY: process.env.REACT_APP_PENDO_KEY || "",
  STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
  ACCESS_JWT: "",
  API_KEY: process.env.REACT_APP_API_KEY || "",
  GET_CONFIG_ERROR: false,
  IS_CORS_ERROR: false,
  USE_DIRECT_GOOGLE_IMAGE: process.env.REACT_APP_USE_DIRECT_GOOGLE_IMAGE || "false",
  ENABLE_GA_CUSTOM_EVENTS: process.env.REACT_APP_ENABLE_GA_CUSTOM_EVENTS === "true",
  APP_VERSION: "1.1.19",
  EXEMPTIPS: ["52.55.200.236", "182.65.205.204", "52.23.27.58"],
  // ACTIVITY_PRODUCT_CODE: "112604P2",
};

let {
  SOCKET_URL,
  environment,
  APP_URL,
  ENDPOINT,
  GOOGLE_ANALYTICS_MEASUREMENT_ID,
  GOOGLE_MAP_API_KEY,
  PUSHER_KEY,
  PUSHER_CLUSTER,
  WEATHER_API,
  WEB_URL,
  GOOGLE_AUTH_CLIENT_ID,
  CLOUDFARE_VERIFICATION_ID,
  STREAMING_ENABLE,
  ENCRYPTION_KEY,
  RADAR,
  IS_ENV_LOADED,
  ENABLE_CRYPTO,
  PENDO_KEY,
  STRIPE_PUBLISHABLE_KEY,
  ACCESS_JWT,
  API_KEY,
  GET_CONFIG_ERROR,
  IS_CORS_ERROR,
  APP_VERSION,
  EXEMPTIPS,
  ACTIVITY_PRODUCT_CODE,
  USE_DIRECT_GOOGLE_IMAGE,
  ENABLE_GA_CUSTOM_EVENTS,
} = ENVIRONMENT || {};

// Amplify builds both app-dev and app-booking-test with REACT_APP_ENVIRONMENT="development",
// so it can't distinguish them. Tag the booking-test domain as "test" explicitly (keyed off the
// live hostname / APP_URL, the same signal environment.ts already uses for getConfigURLForBookingTest);
// every other domain falls back to REACT_APP_ENVIRONMENT. This is the value written to Itinerary.bookedEnv.
const isBookingTestDomain =
  (typeof window !== "undefined" && window.location?.hostname?.includes("app-booking-test")) ||
  (APP_URL || "").includes("app-booking-test");
const bookedEnv = isBookingTestDomain ? ENVIRONMENT_TYPE.TEST : environment;

const isCorsError = (error: any): boolean => {
  // Check for common CORS error indicators
  if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
    return true;
  }

  // Check for CORS-specific error messages
  if (error.message?.toLowerCase().includes("cors")) {
    return true;
  }

  // Check for blocked by CORS policy
  if (error.message?.includes("blocked by CORS policy")) {
    return true;
  }

  // Check for preflight errors
  if (error.message?.includes("preflight")) {
    return true;
  }

  // Check for Access-Control-Allow-Origin errors
  if (error.message?.includes("Access-Control-Allow-Origin")) {
    return true;
  }

  // Check response status and headers for CORS issues
  if (error.response?.status === 0) {
    return true; // Status 0 often indicates CORS or network issues
  }

  if (error.response?.status === 403) {
    return true; // 403 can indicate CORS-related access denial
  }

  return false;
};

// Resolves with the passKey (guest) JWT the moment it is decoded — before
// getFEConfig — or "" if passKey fails. useAppInitialization hands it to the app
// from here; it used to poll ACCESS_JWT every 500 ms, so the first config /
// system-prompt calls of a first visit started up to half a second late.
let resolveGuestToken: (token: string) => void = () => {};
export const guestTokenPromise: Promise<string> = new Promise((resolve) => {
  resolveGuestToken = resolve;
});

async function loadConfig() {
  try {
    const accessTokenURL = `${ENVIRONMENT.ENDPOINT}/app/v1/auth/passKey`;
    const getConfigURL = `${ENVIRONMENT.ENDPOINT}/app/v1/getFEConfig`;
    const getConfigURLForBookingTest = `${ENVIRONMENT.ENDPOINT}/app/v1/getFEConfigForBookingTest`;

    // Fetch access token first (needed for config call)
    const accessTokenResponse = await axios.post(
      accessTokenURL,
      {},
      {
        headers: {
          "x-api-key": ENVIRONMENT.API_KEY || "",
          "Content-Type": "text/plain",
        },
      }
    );
    const decryptedAccessTokenData = decryptData(accessTokenResponse.data);
    const accessJWT = JSON.parse(decryptedAccessTokenData);
    ACCESS_JWT = accessJWT?.data;
    resolveGuestToken(ACCESS_JWT || "");

    const response = await axios({
      url: ENVIRONMENT.APP_URL?.includes("app-booking-test") ? getConfigURLForBookingTest : getConfigURL,
      method: "GET",
      headers: {
        "Content-Type": "text/plain",
        "x-access-token": accessJWT?.data,
        "x-api-key": ENVIRONMENT.API_KEY || "",
      },
    });
    if (response?.data) {
      const parsedData = decryptData(response.data);
      const parsed = JSON.parse(parsedData);
      if (parsedData) {
        const rawBackendConfig = parsed.data || {};
        const backendConfig: Record<string, any> = Object.fromEntries(
          Object.entries(rawBackendConfig).map(([key, value]) => [
            key,
            typeof value === "string" ? value.trim() : value,
          ])
        );
        GOOGLE_ANALYTICS_MEASUREMENT_ID =
          backendConfig.GOOGLE_ANALYTICS_MEASUREMENT_ID || ENVIRONMENT.GOOGLE_ANALYTICS_MEASUREMENT_ID;
        GOOGLE_MAP_API_KEY = backendConfig.GOOGLE_MAP_API_KEY || ENVIRONMENT.GOOGLE_MAP_API_KEY;
        WEATHER_API = { API_KEY: backendConfig.WEATHER_API_KEY || ENVIRONMENT.WEATHER_API.API_KEY };
        GOOGLE_AUTH_CLIENT_ID = backendConfig.GOOGLE_AUTH_CLIENT_ID || ENVIRONMENT.GOOGLE_AUTH_CLIENT_ID;
        CLOUDFARE_VERIFICATION_ID = backendConfig.CLOUDFARE_VERIFICATION_ID || ENVIRONMENT.CLOUDFARE_VERIFICATION_ID;
        ENCRYPTION_KEY = backendConfig.ENCRYPTION_KEY || ENVIRONMENT.ENCRYPTION_KEY;
        RADAR = { API_KEY: backendConfig.RADAR_API_KEY || ENVIRONMENT.RADAR.API_KEY };
        PENDO_KEY = backendConfig.PENDO_KEY || ENVIRONMENT.PENDO_KEY;
        STRIPE_PUBLISHABLE_KEY = ENVIRONMENT.STRIPE_PUBLISHABLE_KEY || backendConfig.STRIP_PK || STRIPE_PUBLISHABLE_KEY;

        API_KEY = backendConfig.API_KEY || ENVIRONMENT.API_KEY;

        IS_ENV_LOADED = true;
        GET_CONFIG_ERROR = false;
      }
    }
  } catch (error) {
    if (isCorsError(error)) {
      IS_CORS_ERROR = true;
    } else {
      GET_CONFIG_ERROR = false;
    }
    consoleLog(["Error fetching backend config:", error]);
    consoleLog("Using default configuration values");
  } finally {
    // No-op once resolved above; settles waiters if passKey itself failed.
    resolveGuestToken(ACCESS_JWT || "");
  }
}

let configLoad: Promise<void> | null = null;

const initializeConfig = (): Promise<void> => {
  configLoad = configLoad ?? loadConfig();
  return configLoad;
};

export {
  initializeConfig,
  ENCRYPTION_KEY,
  SOCKET_URL,
  CLOUDFARE_VERIFICATION_ID,
  environment,
  bookedEnv,
  WEB_URL,
  APP_URL,
  ENDPOINT,
  GOOGLE_ANALYTICS_MEASUREMENT_ID,
  GOOGLE_MAP_API_KEY,
  PUSHER_KEY,
  PUSHER_CLUSTER,
  WEATHER_API,
  GOOGLE_AUTH_CLIENT_ID,
  STREAMING_ENABLE,
  RADAR,
  IS_ENV_LOADED,
  ENABLE_CRYPTO,
  PENDO_KEY,
  STRIPE_PUBLISHABLE_KEY,
  ACCESS_JWT,
  API_KEY,
  GET_CONFIG_ERROR,
  IS_CORS_ERROR,
  APP_VERSION,
  EXEMPTIPS,
  ACTIVITY_PRODUCT_CODE,
  USE_DIRECT_GOOGLE_IMAGE,
  ENABLE_GA_CUSTOM_EVENTS,
};
